/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Real-Time Wallet Balance Hook with Optimistic Invalidation (Task 10.1.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWalletStore } from '../stores/useWalletStore';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useAuthStore } from '../stores/useAuthStore';
import { getApiBaseUrl } from '../lib/api-client';

export type WalletHealthStatus = 'healthy' | 'warning' | 'critical';

export interface UseWalletBalanceReturn {
  balance: number;
  reservedBalance: number;
  lifetimeCredits: number;
  monthlyQuota: number;
  percentageRemaining: number;
  percentageUsed: number;
  renewalDate: string;
  status: WalletHealthStatus;
  statusColor: {
    bg: string;
    text: string;
    border: string;
    dot: string;
  };
  formattedBalance: string;
  formattedQuota: string;
  isLoading: boolean;
  isError: boolean;
  isLowBalance: boolean;
  optimisticDeduct: (amount: number) => { success: boolean; newBalance: number };
  rollbackDeduct: (amount: number) => void;
  topupCredits: (amount: number) => void;
  refetchWallet: () => Promise<void>;
}

export function formatCreditNumber(amount: number): string {
  if (isNaN(amount)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function calculateWalletPercentageUsed(percentageRemaining: number): number {
  return Math.max(0, Math.min(100, Number((100 - percentageRemaining).toFixed(1))));
}

export function formatNextRenewalDate(): string {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return nextMonth.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function calculateWalletStatus(balance: number, monthlyQuota: number): {
  percentageRemaining: number;
  status: WalletHealthStatus;
} {
  if (monthlyQuota <= 0) {
    return {
      percentageRemaining: balance > 0 ? 100 : 0,
      status: balance > 0 ? 'healthy' : 'critical',
    };
  }

  const rawPercent = (balance / monthlyQuota) * 100;
  const percentageRemaining = Math.max(0, Math.min(100, Number(rawPercent.toFixed(1))));

  let status: WalletHealthStatus = 'healthy';
  if (balance <= 0 || percentageRemaining < 5) {
    status = 'critical';
  } else if (percentageRemaining <= 20) {
    status = 'warning';
  }

  return { percentageRemaining, status };
}

export function useWalletBalance(): UseWalletBalanceReturn {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  const {
    balance,
    reservedBalance,
    lifetimeCredits,
    monthlyQuota,
    setWallet,
    optimisticDeduct,
    rollbackDeduct,
    topupCredits,
  } = useWalletStore();

  // TanStack Query to fetch authoritative balance from Cloudflare D1 Ledger
  const {
    data: remoteWallet,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['wallet', activeWorkspaceId],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      try {
        const baseUrl = getApiBaseUrl();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'x-workspace-id': activeWorkspaceId,
        };
        const token = useAuthStore.getState().accessToken;
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const res = await fetch(`${baseUrl}/api/v1/billing/wallet`, { headers });
        if (res.ok) {
          const json = (await res.json()) as {
            data?: {
              workspaceId: string;
              balance: number;
              reservedBalance: number;
              lifetimeCredits: number;
              updatedAt: number;
            };
          };
          if (json?.data) {
            return json.data;
          }
        }
      } catch (err) {
        console.warn('[useWalletBalance] Remote ledger fetch failed, using local store:', err);
      }
      return null;
    },
    enabled: !!activeWorkspaceId,
    staleTime: 30000, // 30 seconds
  });

  // Sync remote data into Zustand store when received
  useEffect(() => {
    if (remoteWallet && activeWorkspaceId) {
      setWallet({
        workspaceId: activeWorkspaceId,
        balance: remoteWallet.balance,
        reservedBalance: remoteWallet.reservedBalance,
        lifetimeCredits: remoteWallet.lifetimeCredits,
        updatedAt: remoteWallet.updatedAt,
      });
    }
  }, [remoteWallet, activeWorkspaceId, setWallet]);

  // Synchronize events across windows/sub-systems
  useEffect(() => {
    // 1. Listen for AI Stream completion events to invalidate D1 cache
    const handleAiStreamCompleted = () => {
      if (activeWorkspaceId) {
        void queryClient.invalidateQueries({ queryKey: ['wallet', activeWorkspaceId] });
      }
    };

    // 2. Listen for optimistic deduct triggers from other modules
    const handleOptimisticDeductEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ amount: number }>;
      if (customEvent.detail?.amount) {
        optimisticDeduct(customEvent.detail.amount);
      }
    };

    window.addEventListener('sophia:ai-stream-completed', handleAiStreamCompleted);
    window.addEventListener('sophia:wallet-optimistic-deduct', handleOptimisticDeductEvent);

    return () => {
      window.removeEventListener('sophia:ai-stream-completed', handleAiStreamCompleted);
      window.removeEventListener('sophia:wallet-optimistic-deduct', handleOptimisticDeductEvent);
    };
  }, [activeWorkspaceId, queryClient, optimisticDeduct]);

  // Calculations
  const { percentageRemaining, status } = useMemo(
    () => calculateWalletStatus(balance, monthlyQuota),
    [balance, monthlyQuota],
  );

  const percentageUsed = useMemo(
    () => calculateWalletPercentageUsed(percentageRemaining),
    [percentageRemaining],
  );

  const renewalDate = useMemo(() => formatNextRenewalDate(), []);

  const statusColor = useMemo(() => {
    switch (status) {
      case 'critical':
        return {
          bg: 'bg-rose-500/10 text-rose-500',
          text: 'text-rose-500',
          border: 'border-rose-500/30',
          dot: 'bg-rose-500 animate-pulse',
        };
      case 'warning':
        return {
          bg: 'bg-amber-500/10 text-amber-500',
          text: 'text-amber-500',
          border: 'border-amber-500/30',
          dot: 'bg-amber-500',
        };
      case 'healthy':
      default:
        return {
          bg: 'bg-emerald-500/10 text-emerald-500',
          text: 'text-emerald-500',
          border: 'border-emerald-500/30',
          dot: 'bg-emerald-500',
        };
    }
  }, [status]);

  const formattedBalance = useMemo(() => formatCreditNumber(balance), [balance]);
  const formattedQuota = useMemo(() => formatCreditNumber(monthlyQuota), [monthlyQuota]);

  const refetchWallet = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    balance,
    reservedBalance,
    lifetimeCredits,
    monthlyQuota,
    percentageRemaining,
    percentageUsed,
    renewalDate,
    status,
    statusColor,
    formattedBalance,
    formattedQuota,
    isLoading,
    isError,
    isLowBalance: status === 'critical',
    optimisticDeduct,
    rollbackDeduct,
    topupCredits,
    refetchWallet,
  };
}
